import { CircularProgress, TextField } from "@mui/material";
import MDBox from "components/MDBox";
import useEditableField from "hooks/useEditableField";
import { memo } from "react";

interface AlwaysEditableFieldProps {
  value: string;
  onSave: (value: string) => Promise<string | void>;
  formatDisplay?: (value: string) => string;
  parseInput?: (value: string) => string;
  type?: "text" | "currency" | "percentage";
  disabled?: boolean;
  fontSize?: string;
  textAlign?: "left" | "right";
  validate?: (value: string) => boolean;
}

function AlwaysEditableField({
  value,
  onSave,
  formatDisplay,
  parseInput,
  type = "text",
  disabled = false,
  fontSize = "12px",
  textAlign = "right",
  validate,
}: AlwaysEditableFieldProps) {
  const field = useEditableField({ value, onSave, formatDisplay, parseInput, validate });
  const isNumeric = type === "currency" || type === "percentage";

  return (
    <MDBox display="flex" alignItems="center">
      <TextField
        value={field.displayValue}
        onChange={(e) => field.onChange(e.target.value)}
        onKeyDown={field.onKeyDown}
        onBlur={field.onBlur}
        onFocus={field.onFocus}
        size="small"
        variant="outlined"
        disabled={disabled}
        error={field.hasError}
        fullWidth
        InputProps={{
          sx: {
            fontSize,
            height: "24px",
            borderRadius: "6px",
            "& input": { textAlign, py: 0, px: "6px" },
            ...(field.hasError && {
              backgroundColor: "rgba(211, 47, 47, 0.1)",
            }),
          },
        }}
        inputProps={{
          inputMode: isNumeric ? "decimal" : undefined,
          pattern: isNumeric ? "[0-9]*[.]?[0-9]*" : undefined,
        }}
      />
      {field.isSaving && <CircularProgress size={12} sx={{ ml: 0.5 }} />}
    </MDBox>
  );
}

export default memo(AlwaysEditableField);
